import { useState } from "react";
import type { BillingClientSummary } from "@school-sales-ai/shared";
import { useSetStripeCustomer } from "@/hooks/useBilling";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type StripeCustomerModalProps = {
  client: BillingClientSummary;
  onClose: () => void;
};

// Same rule the server enforces (setStripeCustomerSchema). Duplicated here only to
// fail fast in the form; the server remains the authority.
const CUSTOMER_ID_PATTERN = /^cus_[A-Za-z0-9]+$/;

export function StripeCustomerModal({
  client,
  onClose,
}: StripeCustomerModalProps): React.JSX.Element {
  const [value, setValue] = useState(client.stripeCustomerId ?? "");
  const [error, setError] = useState<string | null>(null);
  const setCustomer = useSetStripeCustomer();

  function submit(): void {
    const trimmed = value.trim();
    if (trimmed && !CUSTOMER_ID_PATTERN.test(trimmed)) {
      // Pasting a `sub_`, a `price_` or a Payment Link id is the realistic mistake,
      // and it would route no events at all — silently.
      setError("Debe empezar con «cus_» (no es el ID de la suscripción ni del precio).");
      return;
    }
    setError(null);
    setCustomer.mutate(
      { clientId: client.clientId, stripeCustomerId: trimmed || null },
      { onSuccess: onClose },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Vincular cliente de Stripe</DialogTitle>
          <DialogDescription>
            {client.clientName} — pega el Customer ID que Stripe generó al cerrar la venta.
            Es lo único que se captura a mano: el estado de la suscripción y el historial
            los escribe Stripe por webhook.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-ink-700">Stripe Customer ID</label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="cus_..."
            className="font-mono"
            autoFocus
          />
          {error ? (
            <p className="text-xs text-[oklch(0.55_0.18_16)]">{error}</p>
          ) : (
            <p className="text-xs text-ink-500">
              Déjalo vacío para desvincular. Los cobros que hayan llegado antes de
              vincular se recuperan automáticamente.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" onClick={submit} disabled={setCustomer.isPending}>
            {setCustomer.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
